import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import usePages from '../../context/PageContext';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';
import productionService from '../../service/production.service';
import jobMonitorService from '../../service/jobMonitor.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';

import { AttachmentDialogUI, AutoCompleteSelectUI, DebouncedInputUI, FormLabelUI, SwitchUI } from '../Interface';
import JobMonitorCard from './JobMonitorCard';
import ProductDialog from './ProductDialog';
import JobMonitorResources from './JobMonitorResources';
import resourceOneService from '../../service/resources/resourceOne.service';
import TaskFeedbackDialog from '../TaskFeedback/TaskFeedbackDialog';

export const JobMonitorTaskAssigmentPage = () => {
  const navigate = useNavigate();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const saveView = useSaveView(user);
  const jobMonitorTaskAssigmentFilter = useView('jobMonitorTaskAssigmentFilter', user);
  const { menus, getMenus, getMenuSubMenuId, getSubMenuNamesByUrls } = useMenus();
  const { setPages } = usePages();
  const [prodOperations, setProdOperations] = useState({ rows: [], rowCount: 0 });
  const [submenu, setSubmenu] = useState(null);
  const [production, setProduction] = useState({});
  const [productDialogIsOpen, setProductDialogIsOpen] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState({ resourceId: null, index: null });
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    resourceOne: [],
    operations: [],
    status: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [resources, setResources] = useState([]);
  const [selectedResources, setSelectedResources] = useState({ id: [] });
  const [refetchResources, setRefetchResources] = useState(false);
  const [check, setCheck] = useState(false);
  const [prevStatus, setPrevStatus] = useState(false);
  const [filteredResources, setFilteredResources] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const [isFeedBackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});

  const [query, setQuery] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [],
    },
  });

  useEffect(() => {
    getMenus();
    getParam();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0 && query.filter.filterModel.items.length > 0) {
      getList();
    }
  }, [query]);

  useEffect(() => {
    if (selectedResources?.id?.length > 0 && autoCompleteData?.resourceOne) {
      getResourcesByGroupId();
    } else {
      setResources([]);
    }
  }, [selectedResources?.id, autoCompleteData?.resourceOne, refetchResources, query.filter]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('production');
    setSubmenu(submenuId);
  }, [menus]);

  useEffect(() => {
    if (resources?.length > 0) {
      if (check) {
        const filtered = resources.map((resource) => {
          const filteredProductionOperation = resource.productionOperation.filter(
            (operation) => operation.productionStatusId === 'COMPLETED'
          );
          return {
            ...resource,
            productionOperation: filteredProductionOperation,
          };
        });
        setFilteredResources(filtered);
      } else {
        setFilteredResources(resources);
      }
    }
  }, [check, resources]);

  useEffect(() => {
    if (Object.keys(jobMonitorTaskAssigmentFilter).length > 0) {
      const { finishedItemCode, ...filteredFilter } = jobMonitorTaskAssigmentFilter;
      updateQueryForStatusId(false);

      if ('resourceOneId' in filteredFilter) {
        if (filteredFilter.resourceOneId?.length > 0) {
          setSelectedResources((prev) => ({
            ...prev,
            id: filteredFilter.resourceOneId,
          }));
        }
      }

      const filters = Object.entries(filteredFilter).map(([field, value]) => ({ field, value }));
      if (filters.length > 0) {
        handleFilterChange(filters);
      }
    } else {
      updateQueryForStatusId(false);
    }
  }, [jobMonitorTaskAssigmentFilter, params]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['productionreport']);
  }, [menus]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .operationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .productionStatusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          resourceOne: resp[0],
          operations: resp[1],
          items: resp[2],
          status: resp[3],
        };

        setAutoCompleteData(aData);
        const filteredResourceOne = aData.resourceOne?.filter((r1) => r1.name.startsWith('PUFFER'));

        const acOptions = {
          resourceOne: generateAutocompleteItems(filteredResourceOne, 'name', 'id'),
          operations: generateAutocompleteItems(aData.operations, 'operationName', 'operationId'),
          items: generateAutocompleteItems(aData.items, ['itemCode', 'itemName'], 'itemCode'),
          status: generateAutocompleteItems(aData.status, 'name', 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const getResourcesByGroupId = () => {
    const resourceOneId = selectedResources?.id?.map((r) => r.value) ?? [];

    const matchedResources = autoCompleteData?.resourceOne?.filter((r1) => resourceOneId.includes(r1.id)) ?? [];

    const groupId = Array.from(
      new Set(
        matchedResources
          .flatMap(
            (r) => r.operation?.flatMap((op) => op.operationGroupLink?.map((link) => link.operationGroup?.id)) ?? []
          )
          .filter(Boolean)
      )
    );

    const data = {
      ...query,
      filter: {
        ...query.filter,
        filterModel: {
          items: [
            ...query.filter.filterModel.items,
            {
              field: 'groupId',
              operator: 'isAnyOf',
              value: groupId,
            },
          ],
          logicOperator: 'and',
        },
      },
    };

    if (groupId.length) {
      resourceOneService.getResourcesByGroupId(data).then((data) => {
        setResources(data);
        setRefetchResources(false);
      });
    }
  };

  const getList = () => {
    jobMonitorService
      .getProductionOperationsSortedByDeadline(query)
      .then((data) => {
        setProdOperations(data);
      })
      .finally(() => {});
  };

  const updateQueryForStatusId = (usePreviousJobs) => {
    if (!params || !params.JOBMONITOR_STATUSES) {
      return;
    }

    const statusConfig = JSON.parse(params.JOBMONITOR_STATUSES);
    const statusIds = !usePreviousJobs && statusConfig.noPreviousWork;
    const newItems = query.filter.filterModel.items
      .filter((item) => item.field !== 'productionStatusId')
      .concat({
        field: 'productionStatusId',
        operator: 'isAnyOf',
        value: statusIds,
      });

    if (JSON.stringify(newItems) !== JSON.stringify(query.filter.filterModel.items)) {
      setQuery((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            items: newItems,
          },
        },
      }));
    }
  };

  const handleFilterChange = (field, value) => {
    setQuery((prev) => {
      let newItems = prev.filter.filterModel.items;

      if (Array.isArray(field)) {
        newItems = newItems.filter((item) => !field.some((f) => f.field === item.field));

        const validFilters = field
          .filter((f) => f.value !== null && f.value !== undefined && (!Array.isArray(f.value) || f.value.length > 0))

          .map((f) => {
            const rawValue = f.value?.value ?? f.value;
            const processedValue = Array.isArray(rawValue) ? rawValue.map((v) => v.value ?? v) : rawValue;

            return {
              field: f.field,
              operator: Array.isArray(processedValue) ? 'isAnyOf' : 'is',
              value: processedValue,
            };
          });

        newItems = newItems.concat(validFilters);
      } else {
        newItems = newItems.filter((item) => item.field !== field);
        if (value !== null && value !== undefined) {
          const processedValue = Array.isArray(value) ? value.map((v) => v.value ?? v) : value;

          newItems.push({
            field,
            operator: Array.isArray(processedValue) ? 'isAnyOf' : 'is',
            value: processedValue,
          });
        }
      }

      return {
        ...prev,
        filter: {
          filterModel: {
            items: newItems,
          },
        },
      };
    });
  };

  const handleNavigate = (prodOperationId) => {
    setPages({ subMenuName: submenuNames });
    navigate(`/app/productionReport/${prodOperationId}`);
  };

  const getProductionById = (id) => {
    productionService.getProductionById(id).then((data) => {
      setProduction(data);
      setProductDialogIsOpen(true);
    });
  };

  const handleDropAndResourceUpdate = (resourceOneId, jobItem) => {
    productionService
      .updateProdOperationResource(jobItem.id, resourceOneId)
      .then(() => {
        setProdOperations((prev) => {
          return {
            ...prev,
            rows: prev.rows
              .map((row) => {
                if (row.id === jobItem.id) {
                  if (row.productionOperationMultiResource?.length > 0) {
                    return {
                      ...row,
                      removedMain: true,
                    };
                  } else {
                    return null;
                  }
                }
                return row;
              })
              .filter(Boolean),
          };
        });

        setRefetchResources(true);
      })
      .finally(() => {});
  };

  const handleDropAndMultiResourceUpdate = (resourceOneId, jobItem) => {
    productionService
      .updateProductionOperationMultiResource(resourceOneId, jobItem.multiResourceId)
      .then(() => {
        setProdOperations((prev) => ({
          ...prev,
          rows: prev.rows.map((row) => ({
            ...row,
            productionOperationMultiResource: row.productionOperationMultiResource.filter(
              (res) => res.id !== jobItem.multiResourceId
            ),
          })),
        }));

        setRefetchResources(true);
      })
      .finally(() => {});
  };

  const onDragStart = (event, jobItem) => {
    event.dataTransfer.setData('jobItem', JSON.stringify(jobItem));
    setDraggingItemId(jobItem.id);
  };

  const onDrop = (event, resourceOne) => {
    event.preventDefault();

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const isGroupLeader = resourceOne.webUser?.find((user) => user.resourceOneId === resourceOne.id)?.groupLeader;

    if (
      jobItem.operation.groupReporting &&
      jobItem.operation.multiManOperation > 1 &&
      isGroupLeader &&
      !jobItem.isMultiResource
    ) {
      handleDropAndResourceUpdate(resourceOne.id, jobItem);
    } else if (
      jobItem.operation.groupReporting &&
      jobItem.operation.multiManOperation > 1 &&
      !isGroupLeader &&
      !jobItem.isMultiResource
    ) {
      addNotification({
        content: 'A művelet csak csoportvezetőre helyezhető!',
        type: NotificationType.WARNING,
      });
    } else if (jobItem.isMultiResource) {
      handleDropAndMultiResourceUpdate(resourceOne.id, jobItem);
    } else {
      handleDropAndResourceUpdate(resourceOne.id, jobItem);
    }
  };

  const onDropToUnassigned = (event) => {
    event.preventDefault();

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const resourceOneId = jobItem?.operation?.resourceOneId;

    if (!resourceOneId) {
      addNotification({
        content: 'Az alapértelmezett erőforrás nem található.',
        type: NotificationType.ERROR,
      });
      return;
    }

    productionService.updateProdOperationResource(jobItem.id, resourceOneId).then(() => {
      setProdOperations((prev) => ({
        ...prev,
        rows: [...prev.rows, { ...jobItem, removedMain: false }],
      }));

      setRefetchResources(true);
    });
  };

  const extendedRows = prodOperations.rows.flatMap((row) => {
    const selectedResourceIds = selectedResources?.id?.map((res) => res.value) ?? [];

    const relatedMultiResources = row.productionOperationMultiResource
      .filter((multiResource) => selectedResourceIds.includes(multiResource.resourceOneId))
      .map((multiResource) => ({
        ...row,
        id: multiResource.id,
        isMultiResource: true,
        multiResourceId: multiResource.id,
      }));

    const showMain = !row.removedMain;
    const mainResourceIncluded = selectedResourceIds.includes(row.resourceOneId);

    const result = [];

    if (showMain && mainResourceIncluded) {
      result.push(row);
    }

    return [...result, ...relatedMultiResources];
  });

  const onFeedbackClick = (data) => {
    setIsFeedbackDialogOpen(true);
    setFeedbackData({
      productionId: data.productionId,
      prodOperationId: data.id,
      prodOperationLabel: data.label,
      createdBy: user.userId,
    });
  };

  const updateResourceOperations = (resourceId, newOperations, newMultiResources = []) => {
    setResources((prev) =>
      prev.map((res) => {
        if (res.id !== resourceId) return res;

        return {
          ...res,
          productionOperation: [...(res.productionOperation || []), ...newOperations],
          productionOperationMultiResource: [...(res.productionOperationMultiResource || []), ...newMultiResources],
        };
      })
    );
  };

  return (
    <div className="bg-[#CDDFF7] px-6">
      <div className="flex justify-between gap-5 flex-wrap">
        <div className="flex gap-x-5 flex-wrap mb-5">
          <div className="w-[200px]">
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Művelet kereső" />}
              dataset={autocompleteOptions.operations}
              onChange={(_e, newVal, reason) => {
                handleFilterChange('operationId', reason === 'clear' ? null : newVal?.value);
                saveView('jobMonitorTaskAssigmentFilter', { operationId: newVal });
              }}
              selectedValue={query.filter.filterModel.items.find((item) => item.field === 'operationId')?.value}
              selectedLabelValue={jobMonitorTaskAssigmentFilter?.operationId?.label}
            />
          </div>
          <div className="w-[320px]">
            <AutoCompleteSelectUI
              limitTags={1}
              dataset={autocompleteOptions.resourceOne}
              label={<FormLabelUI text="Erőforrás kereső" />}
              onChange={(_e, newVal, reason) => {
                handleFilterChange('resourceOneId', reason === 'clear' ? null : newVal);
                saveView('jobMonitorTaskAssigmentFilter', { resourceOneId: newVal });
                setSelectedResources((prev) => ({
                  ...prev,
                  id: newVal,
                }));
              }}
              selectedValue={selectedResources?.id}
              freeSolo
              multiple
              getOptionLabel={(option) => option.label}
            />
          </div>
          <div className="w-[200px]">
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Késztermék" />}
              dataset={autocompleteOptions.items}
              onChange={(_e, newVal, reason) => {
                handleFilterChange('finishedItemCode', reason === 'clear' ? null : newVal?.value);
              }}
              selectedValue={query.filter.filterModel.items.find((item) => item.field === 'finishedItemCode')?.value}
            />
          </div>
          <div className="w-[200px]">
            <DebouncedInputUI
              label="Vevőhivatkozási szám"
              debounceMs={800}
              setQuickFilterSearchValue={(newVal) => {
                handleFilterChange('reference', newVal);
                saveView('jobMonitorTaskAssigmentFilter', { reference: newVal });
              }}
              quickFilterSearchValue={query.filter.filterModel.items.find((item) => item.field === 'reference')?.value}
            />
          </div>
          <div className="w-[200px]">
            <DebouncedInputUI
              label="Alapanyag szűrés"
              debounceMs={800}
              quickFilterSearchValue={query.filter.filterModel.items.find((item) => item.field === 'itemCode')?.value}
              setQuickFilterSearchValue={(newVal) => {
                handleFilterChange('itemCode', newVal);
                saveView('jobMonitorTaskAssigmentFilter', { itemCode: newVal });
              }}
            />
          </div>
          <div className="pt-4">
            <SwitchUI
              label="Foly./Ellenőrzés"
              checked={check}
              onClick={() => {
                setCheck((prev) => !prev);
              }}
            />
          </div>
          <div className="pt-4">
            <SwitchUI
              label="Előző műv. bef."
              checked={prevStatus}
              onClick={() => {
                setPrevStatus((prev) => !prev);
                handleFilterChange('prevStatus', !prevStatus);
              }}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div
          id="scrollableDiv"
          className="col-span-4 min-h-[calc(100vh-135px)]  max-h-[calc(100vh-135px)] overflow-y-scroll custom-scrollbar bg-gray-100 pl-2 pt-2"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropToUnassigned}
        >
          {extendedRows?.length > 0 &&
            extendedRows.map((operation) => (
              <JobMonitorCard
                key={operation.id}
                data={operation}
                handleNavigate={handleNavigate}
                getProductionById={getProductionById}
                setProdOperations={setProdOperations}
                params={params}
                onDragStart={onDragStart}
                setIsAttachmentDialogOpen={setIsAttachmentDialogOpen}
                setAttachments={setAttachments}
                autoCompleteData={autoCompleteData}
                onFeedbackClick={onFeedbackClick}
              />
            ))}
        </div>

        <div className="col-span-8 min-h-[calc(100vh-135px)] max-h-[calc(100vh-135px)] overflow-x-auto overflow-hidden custom-scrollbar">
          <div className="flex justify-start items-start gap-1 pt-0">
            {filteredResources?.map((resource) => (
              <div className="min-w-[350px] max-w-[350px]">
                <JobMonitorResources
                  key={resource.id}
                  resource={resource}
                  getProductionById={getProductionById}
                  onDragStart={onDragStart}
                  onDrop={onDrop}
                  setRefetchResources={setRefetchResources}
                  isDragging={{ draggingItemId, setDraggingItemId }}
                  dragOverData={{ dragOverIndex, setDragOverIndex }}
                  setIsAttachmentDialogOpen={setIsAttachmentDialogOpen}
                  setAttachments={setAttachments}
                  autoCompleteData={autoCompleteData}
                  onFeedbackClick={onFeedbackClick}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <ProductDialog
        open={productDialogIsOpen}
        handleClose={() => setProductDialogIsOpen(false)}
        production={production}
      />
      <AttachmentDialogUI
        open={isAttachmentDialogOpen}
        data={attachments}
        handleClose={() => setIsAttachmentDialogOpen(false)}
      />

      <TaskFeedbackDialog
        open={isFeedBackDialogOpen}
        handleClose={() => setIsFeedbackDialogOpen(false)}
        data={feedbackData}
      />
    </div>
  );
};
